// src/components/Profile/PerfilUsuario.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Container, Card, Alert, Tabs, Tab, Button, Form, Row, Col, Badge } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import MisBeneficios from "../Home/MisBeneficios";

import exploradorBadge from "../../assets/badges/explorer.png";
import cazadorBadge from "../../assets/badges/hunter.png";
import buscadorBadge from "../../assets/badges/pro.png";
import masterBadge from "../../assets/badges/master.png";
import leyendaBadge from "../../assets/badges/legend.png";

const LEVELS = [
  { code: "EXPLORADOR_DESCUENTOS", label: "Explorador de descuentos", min: 0 },
  { code: "CAZADOR_AHORRO", label: "Cazador del ahorro", min: 49 },
  { code: "BUSCADOR_PROFESIONAL", label: "Buscador profesional", min: 149 },
  { code: "MASTER_PROMOCIONES", label: "Master en promociones", min: 299 },
  { code: "LEYENDA_AHORRO", label: "Leyenda del ahorro", min: 500 },
];

const BADGE_IMAGES = {
  EXPLORADOR_DESCUENTOS: exploradorBadge,
  CAZADOR_AHORRO: cazadorBadge,
  BUSCADOR_PROFESIONAL: buscadorBadge,
  MASTER_PROMOCIONES: masterBadge,
  LEYENDA_AHORRO: leyendaBadge,
};

function computeBadgeFromPoints(points) {
  if (points >= 500) return "LEYENDA_AHORRO";
  if (points >= 299) return "MASTER_PROMOCIONES";
  if (points >= 149) return "BUSCADOR_PROFESIONAL";
  if (points >= 49) return "CAZADOR_AHORRO";
  return "EXPLORADOR_DESCUENTOS";
}

function getLevelMeta(badgeCode, pointsTotal) {
  let code = badgeCode || computeBadgeFromPoints(pointsTotal);
  code = String(code || "").toUpperCase();
  let idx = LEVELS.findIndex((lvl) => lvl.code === code);
  if (idx === -1) idx = 0;
  const current = LEVELS[idx];
  const next = LEVELS[idx + 1] || null;
  return {
    code: current.code,
    name: current.label,
    min: current.min,
    nextMin: next ? next.min : null,
  };
}

const PerfilSettings = ({ profile, onProfileUpdated }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
  });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    setForm({
      name: profile?.name || "",
      phone: profile?.phone || "",
      address: profile?.address || "",
      city: profile?.city || "",
      state: profile?.state || "",
    });
  }, [profile]);

  const avatarUrl = profile?.avatar_url || null;

  const mergeLocalUser = (patch) => {
    const raw = localStorage.getItem("user");
    const user = raw ? JSON.parse(raw) : {};
    localStorage.setItem("user", JSON.stringify({ ...user, ...patch }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSaving(true);
    try {
      const res = await api.put("/users/profile", form);
      const updated = res.data?.profile || {};
      onProfileUpdated(updated);
      mergeLocalUser({
        name: updated.name || form.name,
        avatar_url: updated.avatar_url || avatarUrl || null,
      });
      setSuccess("Perfil actualizado correctamente.");
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo guardar tu perfil.");
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setSuccess("");
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      const res = await api.post("/users/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.avatar_url || null;
      if (url) {
        onProfileUpdated({ ...profile, avatar_url: url });
        mergeLocalUser({ avatar_url: url });
        setSuccess("Avatar actualizado.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo subir el avatar.");
    } finally {
      setAvatarUploading(false);
      e.target.value = "";
    }
  };

  return (
    <Card className="mt-3 shadow-sm border-0">
      <Card.Body>
        <h5 className="mb-3">Configuracion de perfil</h5>

        {error && <Alert variant="warning">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Row className="g-3">
          <Col xs={12} md={4}>
            <div className="p-3 border rounded text-center h-100">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Avatar"
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #e2e8f0",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: "50%",
                    margin: "0 auto",
                    background: "#111827",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 30,
                  }}
                >
                  {(form.name || "U").charAt(0).toUpperCase()}
                </div>
              )}

              <Form.Group className="mt-3">
                <Form.Label className="small text-muted">Avatar / logo personal</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  disabled={avatarUploading}
                />
              </Form.Group>
              <small className="text-muted d-block mt-2">
                JPG/PNG/WEBP. Maximo 5MB.
              </small>
            </div>
          </Col>

          <Col xs={12} md={8}>
            <Form onSubmit={handleSave}>
              <Row className="g-3">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Nombre</Form.Label>
                    <Form.Control
                      value={form.name}
                      onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Tu nombre"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Telefono</Form.Label>
                    <Form.Control
                      value={form.phone}
                      onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="Ej: +54 9 11 1234 5678"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Ciudad</Form.Label>
                    <Form.Control
                      value={form.city}
                      onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                      placeholder="Ciudad"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Provincia / Estado</Form.Label>
                    <Form.Control
                      value={form.state}
                      onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
                      placeholder="Provincia o estado"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Direccion</Form.Label>
                    <Form.Control
                      value={form.address}
                      onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                      placeholder="Direccion"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <div className="d-flex justify-content-end">
                    <Button type="submit" disabled={saving}>
                      {saving ? "Guardando..." : "Guardar cambios"}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const MisPuntos = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const token = localStorage.getItem("token");
    api
      .get("/points/me/history", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setItems(res.data.items || []))
      .catch(() => setError("No pudimos cargar tu historial de puntos."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [pageSize, items.length]);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageItems = items.slice(start, start + pageSize);

  const formatDateTime = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString("es-AR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Card className="mt-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-end mb-3 gap-2">
          <h5 className="mb-0">Historial de puntos</h5>
          {!loading && !error && items.length > 0 && (
            <div style={{ width: 170 }}>
              <small className="text-muted d-block mb-1">Por pagina</small>
              <select
                className="form-select form-select-sm"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
          )}
        </div>

        {loading && <p className="text-muted mb-0">Cargando historial...</p>}
        {error && <Alert variant="warning">{error}</Alert>}
        {!loading && !error && items.length === 0 && (
          <p className="text-muted">Todavia no registraste movimientos.</p>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <ul className="list-unstyled mb-0">
              {pageItems.map((mov) => (
                <li key={mov.id} className="mb-3 pb-2 border-bottom">
                  <div className="d-flex justify-content-between">
                    <div>
                      <strong>{mov.description || "Movimiento"}</strong>
                      <br />
                      <small className="text-muted">{formatDateTime(mov.created_at)}</small>
                    </div>
                    <span
                      style={{
                        fontWeight: "bold",
                        color: mov.points >= 0 ? "green" : "red",
                      }}
                    >
                      {mov.points >= 0 ? `+${mov.points}` : mov.points} pts
                    </span>
                  </div>
                </li>
              ))}
            </ul>

            <div className="d-flex justify-content-between align-items-center mt-2">
              <small className="text-muted">
                Mostrando {start + 1}-{Math.min(start + pageSize, total)} de {total}
              </small>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

const MisResenas = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const localUser = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    const token = localStorage.getItem("token");
    api
      .get("/reviews/my", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const payload = res.data?.items;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : [];
        setItems(list);
      })
      .catch(() => setError("No pudimos cargar tus reseñas."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [pageSize, items.length]);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageItems = items.slice(start, start + pageSize);

  const formatDateTime = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString("es-AR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderStars = (rating) => {
    const safe = Math.max(1, Math.min(5, Number(rating) || 0));
    return "★".repeat(safe) + "☆".repeat(5 - safe);
  };

  const getStatusVariant = (status) => {
    if (status === "approved") return "success";
    if (status === "rejected") return "danger";
    return "warning";
  };

  const getStatusLabel = (status) => {
    if (status === "approved") return "Aprobada";
    if (status === "rejected") return "Rechazada";
    return "Pendiente";
  };

  const renderReviewer = (review) => {
    const name = review?.user_name || localUser?.name || `Usuario #${review?.user_id || ""}`;
    const avatar = review?.user_avatar_url || localUser?.avatar_url || null;
    const initial = String(name || "U").charAt(0).toUpperCase();

    return (
      <div className="d-flex align-items-center gap-2 mb-1">
        {avatar ? (
          <img
            src={avatar}
            alt={name}
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              objectFit: "cover",
              border: "1px solid #e2e8f0",
            }}
          />
        ) : (
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              background: "#111827",
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {initial}
          </div>
        )}
        <span className="text-muted" style={{ fontSize: ".9rem" }}>
          {name}
        </span>
      </div>
    );
  };

  return (
    <Card className="mt-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-end mb-3 gap-2">
          <h5 className="mb-0">Mis reseñas</h5>
          {!loading && !error && items.length > 0 && (
            <div style={{ width: 170 }}>
              <small className="text-muted d-block mb-1">Por pagina</small>
              <select
                className="form-select form-select-sm"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
          )}
        </div>

        {loading && <p className="text-muted mb-0">Cargando reseñas...</p>}
        {error && <Alert variant="warning">{error}</Alert>}
        {!loading && !error && items.length === 0 && (
          <p className="text-muted">Todavia no hiciste reseñas.</p>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <ul className="list-unstyled mb-0">
              {pageItems.map((review) => (
                <li key={review.id} className="mb-3 pb-2 border-bottom">
                  <div className="d-flex justify-content-between gap-2 flex-wrap">
                    <div>
                      {renderReviewer(review)}
                      <strong style={{ color: "#f59e0b", letterSpacing: 1 }}>
                        {renderStars(review.rating)}
                      </strong>
                      <span className="ms-2 text-muted small">{formatDateTime(review.created_at)}</span>
                      <div className="mt-1">
                        <small className="text-muted">
                          Beneficio #{review.benefit_id} | Comercio #{review.business_id}
                        </small>
                      </div>
                      {review.comment && <div className="mt-2">{review.comment}</div>}
                    </div>
                    <Badge bg={getStatusVariant(review.status)}>{getStatusLabel(review.status)}</Badge>
                  </div>
                </li>
              ))}
            </ul>

            <div className="d-flex justify-content-between align-items-center mt-2">
              <small className="text-muted">
                Mostrando {start + 1}-{Math.min(start + pageSize, total)} de {total}
              </small>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

const PerfilUsuario = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [pointsTotal, setPointsTotal] = useState(0);
  const [badgeCode, setBadgeCode] = useState(null);
  const [profileData, setProfileData] = useState({});
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("benefits");

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    const allowed = ["benefits", "points", "reviews", "settings"];
    if (allowed.includes(tab)) setActiveTab(tab);
  }, [location.search]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    Promise.allSettled([
      api.get("/points/me", { headers: { Authorization: `Bearer ${token}` } }),
      api.get("/users/profile", { headers: { Authorization: `Bearer ${token}` } }),
    ])
      .then(([pointsRes, profileRes]) => {
        if (pointsRes.status === "fulfilled") {
          const data = pointsRes.value.data || {};
          setPointsTotal(data.points_total || 0);
          setBadgeCode(data.badge_code || null);
          if (data.user) setUser(data.user);

          const rawUser = localStorage.getItem("user");
          const storedUser = rawUser ? JSON.parse(rawUser) : {};
          localStorage.setItem(
            "user",
            JSON.stringify({
              ...storedUser,
              badge_code: data.badge_code || storedUser.badge_code || null,
              points_total:
                typeof data.points_total === "number"
                  ? data.points_total
                  : storedUser.points_total || 0,
            })
          );
        }

        if (profileRes.status === "fulfilled") {
          const profile = profileRes.value.data || {};
          setProfileData(profile);
          setUser((prev) => ({
            ...(prev || {}),
            id: profile.id || prev?.id,
            email: profile.email || prev?.email,
            name: profile.name || prev?.name,
            avatar_url: profile.avatar_url || null,
          }));

          const raw = localStorage.getItem("user");
          const stored = raw ? JSON.parse(raw) : {};
          localStorage.setItem(
            "user",
            JSON.stringify({
              ...stored,
              name: profile.name || stored.name,
              avatar_url: profile.avatar_url || stored.avatar_url || null,
            })
          );
        }
      })
      .catch(() => setError("No pudimos cargar tu perfil."))
      .finally(() => setLoading(false));
  }, []);

  const levelMeta = useMemo(
    () => getLevelMeta(badgeCode, Number(pointsTotal || 0)),
    [badgeCode, pointsTotal]
  );
  const badgeImgSrc = BADGE_IMAGES[levelMeta.code];

  let progressPercent = 100;
  let remainingToNext = 0;
  if (levelMeta.nextMin !== null) {
    const range = levelMeta.nextMin - levelMeta.min;
    const current = Math.min(pointsTotal - levelMeta.min, range);
    progressPercent = (current / range) * 100;
    remainingToNext = levelMeta.nextMin - pointsTotal;
  }

  if (loading) {
    return (
      <Container className="mt-4">
        <Card className="shadow-sm">
          <Card.Body>Cargando perfil...</Card.Body>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Card className="mb-3 shadow-sm border-0">
        <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3 w-100">
            <div style={{ position: "relative", width: 72, height: 72, flex: "0 0 72px" }}>
              {profileData?.avatar_url ? (
                <img
                  src={profileData.avatar_url}
                  alt={user?.name || "Avatar"}
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #e2e8f0",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    background: "#111827",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 26,
                  }}
                >
                  {(user?.name || "U").charAt(0).toUpperCase()}
                </div>
              )}
              {badgeImgSrc && (
                <img
                  src={badgeImgSrc}
                  alt={levelMeta.name}
                  style={{
                    position: "absolute",
                    right: -4,
                    bottom: -4,
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #fff",
                    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                    background: "#fff",
                  }}
                />
              )}
            </div>

            <div className="flex-grow-1">
              <h3 className="mb-1">{user?.name || "Mi perfil"}</h3>
              <div style={{ fontSize: ".9rem", fontWeight: 600 }}>{levelMeta.name}</div>
              {user?.email && <small className="text-muted">{user.email}</small>}
            </div>
          </div>

          <div style={{ maxWidth: 420, minWidth: 260, width: "100%" }}>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">Puntos acumulados</span>
              <strong>{pointsTotal}</strong>
            </div>
            <div className="progress" style={{ height: 6 }}>
              <div className="progress-bar" role="progressbar" style={{ width: `${progressPercent}%` }} />
            </div>
            {levelMeta.nextMin ? (
              <small className="text-muted d-block mt-1 text-end">
                Te faltan <strong>{remainingToNext}</strong> puntos
              </small>
            ) : (
              <small className="text-muted d-block mt-1 text-end">Nivel maximo alcanzado</small>
            )}
          </div>
        </Card.Body>
      </Card>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || "benefits")} className="mb-3">
        <Tab eventKey="benefits" title="Mis beneficios">
          <MisBeneficios />
        </Tab>
        <Tab eventKey="points" title="Historial de puntos">
          <MisPuntos />
        </Tab>
        <Tab eventKey="reviews" title="Mis reseñas">
          <MisResenas />
        </Tab>
        <Tab eventKey="settings" title="Configuracion">
          <PerfilSettings
            profile={{ ...profileData, name: user?.name || profileData?.name || "" }}
            onProfileUpdated={(updated) => {
              setProfileData((prev) => ({ ...prev, ...updated }));
              setUser((prev) => ({ ...(prev || {}), ...updated }));
            }}
          />
        </Tab>
      </Tabs>

      <div className="d-flex justify-content-end">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate("/")}>
          Volver al inicio
        </Button>
      </div>
    </Container>
  );
};

export default PerfilUsuario;
